import { Box, Button, Popover, TextField, Typography, Grid } from '@mui/material';
import { TZDate } from '@repo/tzdate';
import { createRef, useContext, useState, useEffect } from 'react';
import { useFocusTrap } from 'src/hooks/common/useFocusTrap';
import { useLocale } from '@repo/i18n';
import { MountPointContext } from '@repo/common-utils/mountPoint';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { zIndex } from 'src/utils/widget/zIndex';
import BilberryStaticCalendar from '../../calendar/BilberryStaticCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { capitalize } from '@repo/common-utils/TextUtils';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';

type IProps = {
    anchorEl: HTMLInputElement | HTMLDivElement | null;
    setAnchorEl: React.Dispatch<React.SetStateAction<HTMLDivElement | HTMLInputElement | null>>;
    minDate: TZDate | undefined;
    onSelectDateTime: (date: TZDate | null) => void;
    dateValue: TZDate | null;
};

export default function DateTimePickerPopover(props: IProps) {
    const { anchorEl, setAnchorEl, minDate, onSelectDateTime, dateValue } = props;

    const { t, dayjsLocale } = useLocale();
    const customizations = useCustomizations();
    const config = useConfigurations();
    const mountPoint = useContext(MountPointContext);
    const popoverRef = createRef<HTMLDivElement>();
    const showWeekNumbers = config.showWeekNumbers ?? false;

    // Initialize state for display
    const [displayDate, setDisplayDate] = useState<TZDate | null>(dateValue);
    const [timeValue, setTimeValue] = useState<string>(dateValue ? dateValue.format('HH:mm') : '');

    // Update internal state when props change
    useEffect(() => {
        if (dateValue) {
            setDisplayDate(dateValue);
            setTimeValue(dateValue.format('HH:mm'));
        }
    }, [dateValue]);

    useFocusTrap(popoverRef, Boolean(anchorEl));

    const onClose = () => {
        setAnchorEl(null);
    };

    const disableOkButton = displayDate === null;

    const onOkClicked = () => {
        if (displayDate) {
            // Parse time string if provided, otherwise use 00:00
            let hours = 0;
            let minutes = 0;

            if (timeValue) {
                [hours, minutes] = timeValue.split(':').map(Number);
            }

            // Combine date from displayDate with time
            const dateTime = displayDate.hour(hours).minute(minutes);

            // Call the callback with the new date/time
            onSelectDateTime(dateTime);

            // Close the popover
            setAnchorEl(null);
        }
    };

    const handleTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTimeValue(event.target.value);
    };

    const handleDateChange = (newDate: TZDate | null) => {
        setDisplayDate(newDate);
    };

    return (
        <Popover
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            sx={{
                zIndex: `${zIndex.alwaysVisible} !important` as any,
                '& > .MuiPaper-root': {
                    maxWidth: showWeekNumbers ? 640 : 600,
                    padding: 2,
                },
            }}
            container={mountPoint.popover}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            onClose={onClose}
            disableEnforceFocus={true}
            ref={popoverRef}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                    <BilberryStaticCalendar
                        date={displayDate}
                        onChange={handleDateChange}
                        maxDate={TZDate.now().add(5, 'years')}
                        minDate={minDate}
                        showWeekNumbers={showWeekNumbers}
                    />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        mt: 1,
                        mb: 1,
                    }}
                >
                    <Grid container direction="row" alignItems="center" sx={{ mb: 1 }}>
                        <Typography color="text.secondary">{capitalize(t.select_time)}</Typography>
                        <Box
                            flex="1"
                            borderTop="1px solid"
                            borderColor="text.secondary"
                            ml={1}
                            sx={{
                                opacity: 0.2,
                            }}
                        />
                    </Grid>

                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocale}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'flex-end',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', width: 100 }}>
                                <TextField
                                    type="time"
                                    value={timeValue}
                                    onChange={handleTimeChange}
                                    size="small"
                                    fullWidth
                                    placeholder="00:00"
                                    inputProps={{
                                        step: 300, // 5 min steps
                                    }}
                                />
                            </Box>

                            <Button
                                variant={customizations.primaryButtonStyle}
                                color="primary"
                                onClick={onOkClicked}
                                disabled={disableOkButton}
                            >
                                {t.ok || 'OK'}
                            </Button>
                        </Box>
                    </LocalizationProvider>
                </Box>
            </Box>
        </Popover>
    );
}
